import React, { useState, useEffect } from "react";

const ModifierCustomiser = ({ product, isOpen, onClose, onAdd }) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  const [navIndex, setNavIndex] = useState(0);

  // Initialize defaults
  useEffect(() => {
    if (product) {
      if (product.variants && product.variants.length > 0) {
        setSelectedVariant(product.variants[0]);
      } else {
        setSelectedVariant(null);
      }
      setSelectedAddOns([]);
      setNavIndex(0);
    }
  }, [product]);

  const vCount = product && product.variants ? product.variants.length : 0;
  const aCount = product && product.addOns ? product.addOns.length : 0;
  const totalItems = vCount + aCount + 1; // +1 for the Add to Cart button

  useEffect(() => {
    if (!isOpen || !product) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setNavIndex((prev) => {
          const next = Math.min(prev + 1, totalItems - 1);
          document.querySelector(`[data-modal-nav="${next}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return next;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setNavIndex((prev) => {
          const next = Math.max(prev - 1, 0);
          document.querySelector(`[data-modal-nav="${next}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return next;
        });
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (navIndex < vCount) {
          setSelectedVariant(product.variants[navIndex]);
        } else if (navIndex < vCount + aCount) {
          handleAddOnClick(product.addOns[navIndex - vCount]);
        } else if (navIndex === vCount + aCount) {
          handleAddToCart();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, product, navIndex, selectedVariant, selectedAddOns, vCount, aCount, totalItems]);

  if (!isOpen || !product) return null;

  const hasVariants = product.variants && product.variants.length > 0;
  const hasAddOns = product.addOns && product.addOns.length > 0;

  // Calculate Running Price
  const basePrice = selectedVariant ? Number(selectedVariant.price) : Number(product.price);
  const addOnsTotal = selectedAddOns.reduce((sum, item) => sum + Number(item.price), 0);
  const runningTotal = basePrice + addOnsTotal;

  const handleAddOnClick = (addon) => {
    setSelectedAddOns((prev) => {
      const exists = prev.find((a) => a.name === addon.name);
      if (exists) {
        return prev.filter((a) => a.name !== addon.name);
      }
      return [...prev, addon];
    });
  };

  const handleAddToCart = () => {
    onAdd(product, selectedVariant, selectedAddOns);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-300">
      
      {/* Click Outside to Close */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Customizer Drawer / Container */}
      <div className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] sm:max-h-[75vh] animate-slideInUp z-10">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-start bg-slate-50">
          <div>
            <span className="text-xs font-bold text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full uppercase tracking-wider block w-fit mb-1.5">
              Customize Item
            </span>
            <h3 className="text-lg font-bold text-slate-800">{product.productName}</h3>
            {product.description && (
              <p className="text-xs text-slate-400 mt-1 line-clamp-1">{product.description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-200/60 hover:bg-slate-200 text-slate-500 flex items-center justify-center font-bold text-sm transition-all"
          >
            ✕
          </button>
        </div>

        {/* Scrollable Options */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* VARIANTS (🍕 Mutually Exclusive) */}
          {hasVariants && (
            <div className="space-y-3">
              <div className="flex justify-between items-center border-b pb-2">
                <h4 className="font-bold text-slate-800 text-sm">Choose Option</h4>
                <span className="text-[10px] uppercase font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">Required</span>
              </div>
              <div className="space-y-2">
                {product.variants.map((v, idx) => (
                  <label
                    key={idx}
                    data-modal-nav={idx}
                    className={`flex items-center justify-between p-3.5 border rounded-2xl cursor-pointer transition-all ${
                      selectedVariant?.name === v.name
                        ? "border-indigo-600 bg-indigo-50/40 text-indigo-900 shadow-sm"
                        : "border-slate-200 hover:bg-slate-50 text-slate-700"
                    } ${navIndex === idx ? "ring-2 ring-indigo-400 ring-offset-1 scale-[1.02] z-10" : ""}`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="product-variant"
                        checked={selectedVariant?.name === v.name}
                        onChange={() => setSelectedVariant(v)}
                        className="accent-indigo-600 w-4 h-4"
                      />
                      <span className="text-sm font-semibold">{v.name}</span>
                    </div>
                    <span className="text-sm font-bold">₹{v.price}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* ADD-ONS (🧀 Non-Mutually Exclusive) */}
          {hasAddOns && (
            <div className="space-y-3">
              <div className="flex justify-between items-center border-b pb-2">
                <h4 className="font-bold text-slate-800 text-sm">Add Extras</h4>
                <span className="text-[10px] uppercase font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">Optional</span>
              </div>
              <div className="space-y-2">
                {product.addOns.map((a, idx) => {
                  const isChecked = !!selectedAddOns.find((item) => item.name === a.name);
                  return (
                    <label
                      key={idx}
                      data-modal-nav={vCount + idx}
                      className={`flex items-center justify-between p-3.5 border rounded-2xl cursor-pointer transition-all ${
                        isChecked
                          ? "border-teal-600 bg-teal-50/40 text-teal-900 shadow-sm"
                          : "border-slate-200 hover:bg-slate-50 text-slate-700"
                      } ${navIndex === vCount + idx ? "ring-2 ring-teal-400 ring-offset-1 scale-[1.02] z-10" : ""}`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleAddOnClick(a)}
                          className="accent-teal-600 w-4 h-4 rounded"
                        />
                        <span className="text-sm font-semibold">{a.name}</span>
                      </div>
                      <span className="text-sm font-bold text-teal-600">+ ₹{a.price}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer (running total and add button) */}
        <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-4">
          <div className="flex flex-col">
            <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Total Price</span>
            <span className="text-2xl font-black text-slate-800">₹{runningTotal}</span>
          </div>
          <button
            data-modal-nav={(product.variants ? product.variants.length : 0) + (product.addOns ? product.addOns.length : 0)}
            onClick={handleAddToCart}
            className={`flex-1 font-bold py-3 px-6 rounded-2xl shadow-lg transition-all duration-200 ${
              navIndex === ((product.variants ? product.variants.length : 0) + (product.addOns ? product.addOns.length : 0))
                ? "bg-indigo-700 text-white ring-4 ring-indigo-300 scale-105"
                : "bg-indigo-600 hover:bg-indigo-700 text-white hover:shadow-indigo-500/20 active:scale-95"
            }`}
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModifierCustomiser;
